import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import {
    FactFamily,
    FactFamilyOptions,
    Operations,
    ProblemData,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Fact family / number bond generation. See ADR 0020.
 *
 * The unit is a group: two parts and their whole, plus which operation relates
 * them. A multiplicative family is built from the two factors, so the product
 * always divides evenly. The three numbers and the question type travel in
 * `ProblemData.factFamily`; `numbers` mirrors them and `operator` is empty.
 */

/** Which families a sheet's operator selection asks for. */
function familyKinds(settings: ISettingsProps): ("additive" | "multiplicative")[] {
    const ops = settings.Operators?.map((o) => o.value) ?? [];
    const additive =
        ops.includes(Operations.Addition) || ops.includes(Operations.Subtraction);
    const multiplicative =
        ops.includes(Operations.Multiplication) ||
        ops.includes(Operations.Division) ||
        ops.includes(Operations.LongDivision);
    const kinds: ("additive" | "multiplicative")[] = [];
    if (additive) kinds.push("additive");
    if (multiplicative) kinds.push("multiplicative");
    return kinds.length > 0 ? kinds : ["additive"];
}

/** The largest part value to draw, from the sheet's first number range. */
function partCeiling(settings: ISettingsProps): number {
    const digits = settings.NumberRanges?.[0]?.NumberOfDigits ?? 1;
    // Fact families are about seeing the relationship; keep the numbers small
    // even when the range control allows more (ADR 0020 §5).
    return digits >= 3 ? 40 : digits === 2 ? 20 : 12;
}

export function buildFactFamilyProblem(
    kind: "additive" | "multiplicative",
    question: FactFamilyOptions,
    ceiling: number,
    key: number
): ProblemData {
    let a: number;
    let b: number;
    let whole: number;
    if (kind === "multiplicative") {
        // Built from the two factors so the product always divides evenly
        // (ADR 0020 §3). Times-table factors regardless of the additive ceiling.
        const top = ceiling >= 20 ? 12 : 9;
        a = getRandomInt(2, top);
        b = getRandomInt(2, top);
        whole = a * b;
    } else {
        a = getRandomInt(1, ceiling - 1);
        b = getRandomInt(1, ceiling - a);
        whole = a + b;
    }

    let blank: FactFamily["blank"] = null;
    if (question !== FactFamilyOptions.FourEquations) {
        blank = (["part0", "part1", "whole"] as const)[getRandomInt(0, 2)];
    }

    return {
        key,
        numbers: [a, b, whole],
        operator: "",
        factFamily: {
            parts: [a, b],
            whole,
            multiplicative: kind === "multiplicative",
            kind: question,
            blank,
        },
    };
}

/** A page-shaped set of fact-family problems, matching GenerateDataV2's shape. */
export function generateFactFamilyPages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.FactFamilyQuestion ?? FactFamilyOptions.FourEquations;
    const kinds = familyKinds(settings);
    const ceiling = partCeiling(settings);
    return generateProblemPages({
        settings,
        choices: kinds,
        build: (kind, key) => buildFactFamilyProblem(kind, question, ceiling, key),
        fingerprint: (problem) => {
            const f = problem.factFamily!;
            const kind = f.multiplicative ? "multiplicative" : "additive";
            return `${kind}:${f.parts[0]}|${f.parts[1]}`;
        },
    });
}
